import { Feature } from '@tencent/workbench';
import type { NumberInputFrom } from '@tencent/wb-ext-form';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { ptToCm, cmToPt } from '@src/slide/infrastructure/module/independent/unit';
import get from 'lodash/get';
import { DEFAULT_PADDING_LR, DEFAULT_PADDING_TB } from '@editor/src/page/index/components/editor/config';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '../../../constants';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';

// 修正浮点数相加可能产生的尾数，保留两位
const TO_FIXED = 2;
const MAX_LENGTH = 5;
const DEFAULT_PADDING_PROPS = {
    paddingLeft: DEFAULT_PADDING_LR,
    paddingRight: DEFAULT_PADDING_LR,
    paddingTop: DEFAULT_PADDING_LR,
    paddingBottom: DEFAULT_PADDING_TB,
};

@Feature()
export class TextBoxFeature extends BaseDrawerFeature {
    public paddingProps = { ...DEFAULT_PADDING_PROPS };
    public get textAdjustInfo() {
        return this.getPropertyMenu()?.values?.[ActionType.TEXT_ADJUST];
    }

    public get textDirection(): TextDirectionType {
        return this.getPropertyMenu()?.values?.[FormItemName.TEXT_DIRECTION];
    }

    public get paddingInfo() {
        return this.getPaddingStyle();
    }

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.TEXT_BOX);
    }

    public initPropsCallback = () => {
        this.paddingProps = { ...this.paddingInfo };
    };

    /**
     * 数字组件在编辑模式下，数据的修改应该只触发输入框视图的更新
     * 非编辑模式下，才需要触发数据更新
     * @param type 更新的边距类型
     * @param value 更新的边距值
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_BOX)
    public handleChange(type: string, value: number, from?: NumberInputFrom) {
        let fixedValue = value;
        const toFixedNumber = Math.pow(100, TO_FIXED);
        if (String(value).length > MAX_LENGTH) {
            fixedValue = Math.round(fixedValue * toFixedNumber) / toFixedNumber;
        }
        this.paddingProps[type] = fixedValue;

        const isOnlyUIChange = this.isInputOnlyChangeUI();
        if (isOnlyUIChange) return;

        const changeVal = cmToPt(fixedValue, TO_FIXED);
        this.updateProps(ActionType.PADDING_ADJUST, { [type]: changeVal }, isOnlyUIChange, false);
        this.tdwDrawerReportNumberInput(ActionType.PADDING_ADJUST, { ver6: changeVal, ver7: type }, from);
    }

    /**
     * 获取参数中的初始化/更新的padding，并且将office单位转换成展示的ui单位，emu -> cm
     */
    private getPaddingStyle = () => {
        const rawData = this.textAdjustInfo?.rawData;
        const paddingLeft = this.getDefaultValue(get(rawData, 'paddingLeft'), DEFAULT_PADDING_LR);
        const paddingRight = this.getDefaultValue(get(rawData, 'paddingRight'), DEFAULT_PADDING_LR);
        const paddingTop = this.getDefaultValue(get(rawData, 'paddingTop'), DEFAULT_PADDING_TB);
        const paddingBottom = this.getDefaultValue(get(rawData, 'paddingBottom'), DEFAULT_PADDING_TB);
        return {
            paddingLeft: this.fixValue(paddingLeft),
            paddingRight: this.fixValue(paddingRight),
            paddingTop: this.fixValue(paddingTop),
            paddingBottom: this.fixValue(paddingBottom),
        };
    };

    private fixValue = value => ptToCm(value, TO_FIXED);

    // 初始化时，空值/非法值需要转默认值
    private getDefaultValue = (val, defaultValue) => {
        if (isNaN(val)) {
            return defaultValue;
        }
        return val;
    };
}
